// CAPABILITY: Auth — password reset confirmation (the "set new password" page)
// INPUTS: Firebase email-action link query params (mode, oobCode, continueUrl)
// OUTPUTS: Verified + confirmed password reset via Firebase Auth
// FLOW: Email link -> /reset-password?mode=resetPassword&oobCode=... -> this page
import {
  applyActionCode,
  confirmPasswordReset,
  verifyPasswordResetCode,
} from "firebase/auth";
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AuthShell, { AuthAlert } from "../../components/AuthShell";
import { auth } from "../../../firebase";

// Firebase email action modes we know how to handle in-app. Anything else
// falls back to a friendly "unsupported action" state rather than a blank page.
const MODE_RESET = "resetPassword";
const MODE_VERIFY = "verifyEmail";
const MODE_RECOVER = "recoverEmail";

export default function PasswordResetConfirm() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const passwordRef = useRef();
  const passwordConfirmRef = useRef();

  const mode = params.get("mode");
  const oobCode = params.get("oobCode");

  const [status, setStatus] = useState("initializing"); // initializing|ready|submitting|done|error
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");

  // Verify the action code on mount so we can show a precise error for
  // expired/invalid/reused codes before the user types a new password.
  useEffect(() => {
    let cancelled = false;
    async function verify() {
      if (!oobCode) {
        setStatus("error");
        setError("This link is missing a valid reset code. Please request a new password reset link.");
        return;
      }
      try {
        if (mode === MODE_RESET) {
          const verifiedEmail = await verifyPasswordResetCode(auth, oobCode);
          if (!cancelled) {
            setEmail(verifiedEmail);
            setStatus("ready");
          }
        } else if (mode === MODE_VERIFY || mode === MODE_RECOVER) {
          await applyActionCode(auth, oobCode);
          if (!cancelled) setStatus("done");
        } else {
          if (!cancelled) {
            setStatus("error");
            setError("This link is for an action this app doesn't handle in-app.");
          }
        }
      } catch (err) {
        if (!cancelled) {
          setStatus("error");
          setError(`This link is invalid or has expired. ${err.message || ""}`.trim());
          console.error("PASSWORD_ACTION_VERIFY_FAILED", err);
        }
      }
    }
    verify();
    return () => {
      cancelled = true;
    };
  }, [mode, oobCode]);

  async function handleSubmit(e) {
    e.preventDefault();
    const next = passwordRef.current.value;
    const confirm = passwordConfirmRef.current.value;
    if (next.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (next !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setError("");
    setStatus("submitting");
    try {
      await confirmPasswordReset(auth, oobCode, next);
      setStatus("done");
    } catch (err) {
      setStatus("ready");
      setError(`Could not update your password: ${err.message || "please try again."}`);
      console.error("PASSWORD_RESET_CONFIRM_FAILED", err);
    }
  }

  // --- Done state (reset success OR email verification/recovery applied) ---
  if (status === "done") {
    return (
      <AuthShell
        eyebrow="Account access"
        title={mode === MODE_RESET ? "Password updated" : "Email confirmed"}
        subtitle="Your VdoPage account is ready. You can now sign in with your new credentials."
        footer={
          <Link to="/login" className="font-semibold text-[#FF3A20] hover:text-[#e8321c]">
            Continue to login
          </Link>
        }
      >
        <AuthAlert type="success">
          {mode === MODE_RESET
            ? "Your password has been changed successfully."
            : "Your email has been verified."}
        </AuthAlert>
        <button
          type="button"
          className="primary-button w-full"
          onClick={() => navigate("/login")}
        >
          Go to login
        </button>
      </AuthShell>
    );
  }

  // --- Error / unsupported state ---
  if (status === "error") {
    return (
      <AuthShell
        eyebrow="Account access"
        title="Link not valid"
        subtitle="This password reset link is invalid, expired, or already used."
        footer={
          <Link to="/password-reset" className="font-semibold text-[#FF3A20] hover:text-[#e8321c]">
            Request a new reset link
          </Link>
        }
      >
        <AuthAlert>{error}</AuthAlert>
        <Link to="/password-reset" className="primary-button w-full block text-center">
          Request a new reset link
        </Link>
      </AuthShell>
    );
  }

  // --- Initializing (verifying code) ---
  if (status === "initializing") {
    return (
      <AuthShell
        eyebrow="Account access"
        title="Verifying link"
        subtitle="Checking your reset link…"
      >
        <div className="flex items-center justify-center py-6 text-sm text-slate-500">
          Verifying your reset link…
        </div>
      </AuthShell>
    );
  }

  // --- Ready: set a new password ---
  return (
    <AuthShell
      eyebrow="Account access"
      title="Set a new password"
      subtitle={
        email
          ? `Set a new password for ${email}.`
          : "Choose a new password for your VdoPage account."
      }
      footer={
        <Link to="/login" className="font-semibold text-[#FF3A20] hover:text-[#e8321c]">
          Back to login
        </Link>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {error && <AuthAlert>{error}</AuthAlert>}

        <div className="field-group">
          <label htmlFor="new-password" className="field-label">
            New password
          </label>
          <input
            type="password"
            id="new-password"
            ref={passwordRef}
            required
            minLength={6}
            autoComplete="new-password"
            className="field-input"
            placeholder="At least 6 characters"
          />
        </div>

        <div className="field-group">
          <label htmlFor="confirm-password" className="field-label">
            Confirm new password
          </label>
          <input
            type="password"
            id="confirm-password"
            ref={passwordConfirmRef}
            required
            minLength={6}
            autoComplete="new-password"
            className="field-input"
            placeholder="Repeat new password"
          />
        </div>

        <button type="submit" className="primary-button w-full" disabled={status === "submitting"}>
          {status === "submitting" ? "Updating…" : "Update password"}
        </button>
      </form>
    </AuthShell>
  );
}
